<template>
  <div class="app-navbar-actions-premium">
    <!-- Notifications Dropdown -->
    <VaDropdown v-if="!isMobile" placement="bottom-end" :offset="[0, 12]" class="premium-dropdown-wrap">
      <template #anchor>
        <VaButton preset="plain" aria-label="Notifications" class="premium-nav-btn">
          <VaIcon name="notifications" color="#94a3b8" />
          <div v-if="notificationCount > 0" class="premium-badge-dot"></div>
        </VaButton>
      </template>
      <VaDropdownContent class="premium-dropdown-card">
        <div class="notif-header">
          <span class="notif-title">Notifications</span>
          <VaButton preset="plain" size="small" color="primary" @click="goToNotifications">View All</VaButton>
        </div>
        <div class="notif-list-premium">
          <div v-if="recentNotifications.length === 0" class="empty-notif">No new updates</div>
          <div
            v-for="notification in recentNotifications"
            :key="String((notification as any).id)"
            class="notif-item-premium"
            :class="{ unread: (notification as any).status === 'sent' }"
            @click="markAsRead(notification)"
          >
            <div class="notif-icon emerald">
              <VaIcon name="info" size="small" />
            </div>
            <div class="notif-content">
              <p class="notif-subj">
                {{ (notification as any).message || (notification as any).subject || 'No subject' }}
              </p>
              <p class="notif-time">
                {{ formatTime((notification as any).created_at || (notification as any).sent_at || '') }}
              </p>
            </div>
          </div>
        </div>
      </VaDropdownContent>
    </VaDropdown>

    <!-- Language Switcher -->
    <VaDropdown v-if="!isMobile" placement="bottom-end" :offset="[0, 12]">
      <template #anchor>
        <button class="locale-trigger-premium">
          <span class="flag">{{ currentLocaleFlag }}</span>
          <VaIcon name="expand_more" size="small" color="#64748b" />
        </button>
      </template>
      <VaDropdownContent class="premium-dropdown-card">
        <div
          v-for="opt in localeOptions"
          :key="opt.value"
          class="locale-opt-premium"
          :class="{ active: locale === opt.value }"
          @click="setLocale(opt.value)"
        >
          <span class="opt-flag">{{ getLocaleFlag(opt.value) }}</span>
          <span class="opt-label">{{ opt.label }}</span>
        </div>
      </VaDropdownContent>
    </VaDropdown>

    <!-- Theme Toggle -->
    <VaButton
      v-if="!isMobile"
      preset="plain"
      class="premium-nav-btn theme-toggle-btn"
      :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleTheme"
    >
      <VaIcon :name="isDark ? 'light_mode' : 'dark_mode'" color="primary" />
    </VaButton>

    <!-- Account Dropdown -->
    <VaDropdown placement="bottom-end" :offset="[0, 12]">
      <template #anchor>
        <button class="account-trigger-premium">
          <div class="avatar-ring-premium">
            <div class="avatar-initials-gradient">{{ userInitials }}</div>
          </div>
          <div v-if="!isMobile" class="user-meta">
            <span class="user-name">{{ userName }}</span>
            <span class="user-role">Owner</span>
          </div>
          <VaIcon name="unfold_more" size="small" color="#64748b" />
        </button>
      </template>
      <VaDropdownContent class="premium-dropdown-card account-menu-premium">
        <div class="menu-item-premium" @click="goToSettings">
          <VaIcon name="person_outline" color="#94a3b8" />
          <span>Profile</span>
        </div>
        <div class="menu-divider"></div>
        <div class="menu-item-premium danger" @click="handleLogout">
          <VaIcon name="logout" color="#ef4444" />
          <span>Sign Out</span>
        </div>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../../stores/auth-store'
import { setLocale as setI18nLocale, supportedLocales } from '@/i18n'
import type { Locale } from '@/i18n'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const router = useRouter()
const authStore = useAuthStore()
const { locale } = useI18n({ useScope: 'global' })
const { applyPreset, currentPresetName } = useColors()

const notificationCount = ref(0)
const recentNotifications = ref<any[]>([])
const localeOptions = supportedLocales

// ── Theme ─────────────────────────────────────────────────────
const isDark = computed(() => currentPresetName.value === 'dark')

const toggleTheme = () => {
  const newTheme = isDark.value ? 'light' : 'dark'
  applyPreset(newTheme)
  localStorage.setItem('theme', newTheme)
  document.documentElement.classList.toggle('dark', newTheme === 'dark')
}

onMounted(() => {
  const saved = localStorage.getItem('theme') || 'dark'
  if (saved !== currentPresetName.value) {
    applyPreset(saved)
    document.documentElement.classList.toggle('dark', saved === 'dark')
  }
})

// ── User ──────────────────────────────────────────────────────
const userName = computed(() => authStore.userName || 'User')
const userInitials = computed(() => {
  const name = authStore.userName || ''
  if (!name) return 'US'
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
})

// ── Locale ────────────────────────────────────────────────────
const localeFlags: Record<string, string> = {
  en: '🇺🇸',
  gb: '🇬🇧',
  sw: '🇹🇿',
  br: '🇧🇷',
  cn: '🇨🇳',
  es: '🇪🇸',
  ir: '🇮🇷',
}

const currentLocaleFlag = computed(() => localeFlags[locale.value] ?? '🌐')

function getLocaleFlag(l: string) {
  return localeFlags[l] ?? '🌐'
}

function setLocale(value: Locale) {
  setI18nLocale(value)
}

// ── Notifications ─────────────────────────────────────────────
const formatTime = (dateString: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

const goToNotifications = () => {
  try {
    router.push({ name: 'notifications' })
  } catch {
    /* route may not exist */
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const markAsRead = async (notification: any) => {
  /* placeholder */
}

// ── Account ───────────────────────────────────────────────────
function goToSettings() {
  try {
    router.push({ name: 'account' })
  } catch {
    /* fallback */ router.push('/settings')
  }
}

function handleLogout() {
  authStore.logout()
  router.push('/auth/login')
}
</script>

<style scoped>
.app-navbar-actions-premium {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Search */
.premium-navbar-search {
  width: 220px;
}

.premium-navbar-search :deep(.va-input-wrapper) {
  border-radius: 100px !important;
  border: 1px solid var(--va-background-border) !important;
  background: var(--va-background-element) !important;
}

/* Icon Buttons */
.premium-nav-btn {
  width: 40px;
  height: 40px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 12px !important;
  background: var(--va-background-element) !important;
  position: relative;
  transition: background 0.2s ease;
}

.premium-nav-btn:hover {
  background: rgba(148, 163, 184, 0.1) !important;
}

.theme-toggle-btn {
  background: var(--va-background-element) !important;
}

/* Notification badge dot */
.premium-badge-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);
}

/* Dropdown Card */
.premium-dropdown-card {
  background: var(--va-background-secondary) !important;
  border: 1px solid var(--va-background-border) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
  border-radius: 16px !important;
  padding: 0.5rem !important;
}

/* Notifications */
.notif-header {
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--va-background-border);
  margin-bottom: 0.5rem;
}

.notif-title {
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--va-text-primary);
}

.notif-list-premium {
  width: 300px;
  max-height: 360px;
  overflow-y: auto;
}

.notif-item-premium {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  transition: all 0.2s;
  cursor: pointer;
  align-items: flex-start;
}

.notif-item-premium:hover {
  background: rgba(255, 255, 255, 0.03);
}

.notif-item-premium.unread {
  background: rgba(34, 197, 94, 0.04);
}

.notif-icon.emerald {
  width: 36px;
  height: 36px;
  background: rgba(34, 197, 94, 0.12);
  color: #22c55e;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notif-subj {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--va-text-primary);
  margin: 0 0 0.25rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.notif-time {
  font-size: 0.75rem;
  color: var(--va-text-secondary);
  margin: 0;
}

.empty-notif {
  padding: 2rem;
  text-align: center;
  color: var(--va-text-secondary);
  font-size: 0.875rem;
}

/* Locale Switcher */
.locale-trigger-premium {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--va-background-element);
  border: none;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  color: var(--va-text-primary);
  cursor: pointer;
  transition: background 0.2s;
  font-size: 1rem;
}

.locale-trigger-premium:hover {
  background: rgba(148, 163, 184, 0.1) !important;
}

.locale-opt-premium {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--va-text-secondary);
  font-size: 0.875rem;
}

.locale-opt-premium:hover {
  background: rgba(255, 255, 255, 0.04);
  color: var(--va-text-primary);
}

.locale-opt-premium.active {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.opt-flag {
  font-size: 1.1rem;
}

/* Account Trigger */
.account-trigger-premium {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.375rem 0.75rem;
  background: var(--va-background-element);
  border: 1px solid var(--va-background-border);
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.2s;
}

.account-trigger-premium:hover {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.3);
}

.avatar-ring-premium {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e, #10b981);
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-initials-gradient {
  width: 100%;
  height: 100%;
  background: var(--va-background-primary);
  color: var(--va-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 50%;
}

.user-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--va-text-primary);
}

.user-role {
  font-size: 0.7rem;
  color: #22c55e;
  font-weight: 600;
}

/* Account Menu */
.account-menu-premium {
  width: 200px;
}

.menu-item-premium {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  cursor: pointer;
  color: var(--va-text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
}

.menu-item-premium:hover {
  background: rgba(255, 255, 255, 0.04);
  color: var(--va-text-primary);
}

.menu-item-premium.danger:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.06);
}

.menu-divider {
  height: 1px;
  background: var(--va-background-border);
  margin: 0.4rem 0.5rem;
}
</style>
